/**
 * CORE-M-11 Campaign structure drill-down.
 *
 * Chans's 2026-09-15 rebuild spec §2.11 + Q-2 ruling: Current Campaigns
 * folds into Management as a drill-down (campaign → ad group → ads &
 * keywords) with health flags per campaign, not its own left-nav tab.
 * The workflow is: clear the alerts in the category tabs first, then
 * skim the structure yourself with full context available.
 *
 * Phase A shipped the campaign list. Phase B (this file) adds inline
 * ad-group expansion per campaign — the operator can drill from a
 * campaign row into its ad groups + their last-30-days performance
 * without opening the standalone Campaigns page.
 *
 * Ads + keywords per ad-group land with CORE-M-11.c. Per-campaign
 * health flags (M-10 Ad Health move-in, CORE-M-10.a) land as an inline
 * chip on the campaign row driven by the same ad-monitor read the Ad
 * Review tab consumes.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  Check,
  ChevronDown,
  ChevronRight,
  Link2,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  listAdGroupPerformance,
  listAdPerformance,
  listKeywordPerformance,
  listLiveCampaigns,
  type AdGroupPerformanceItem,
  type AdGroupPerformanceResponse,
  type AdPerformanceItem,
  type AdPerformanceResponse,
  type KeywordPerformanceItem,
  type KeywordPerformanceResponse,
  type LiveCampaignInventoryItem,
} from '@/services/googleAdsCampaignsService';
import {
  getAdMonitor,
  type MonitoredAd,
} from '@/services/googleAdsManagementService';
import { AdsSlowLoad } from '@/features/ads/AdsSlowLoad';
import { useTenantStore } from '@/stores/useTenantStore';

import { useManagementWindowDays } from './useManagementWindow';
import {
  BUSY_ROW_CLASS,
  DrilldownWriteContext,
  dismissAllUndoToasts,
  useDrilldownStatusToggle,
  type DrilldownWriteAccess,
} from './DrilldownStatusToggle';
import { cn } from '@/lib/utils';

interface CampaignDrilldownProps {
  tenantId?: string | null;
}

type StatusFilter = 'enabled' | 'all';

function formatUsdFromMicros(micros: number | null | undefined): string {
  if (typeof micros !== 'number' || !Number.isFinite(micros)) return '—';
  const usd = micros / 1_000_000;
  return `$${usd.toLocaleString(undefined, {
    maximumFractionDigits: usd >= 1000 ? 0 : 2,
  })}`;
}

function statusRank(status: string | null | undefined): number {
  const s = (status ?? '').toUpperCase();
  if (s === 'ENABLED') return 0;
  if (s === 'PAUSED') return 1;
  return 2; // REMOVED / UNKNOWN — bottom
}

/**
 * Sorted campaign list: Enabled first (rank 0), Paused next (rank 1),
 * everything else at the bottom (rank 2). Ties broken by name.
 */
export function sortEnabledFirst(
  campaigns: readonly LiveCampaignInventoryItem[],
): LiveCampaignInventoryItem[] {
  return [...campaigns].sort((a, b) => {
    const ra = statusRank(a.status);
    const rb = statusRank(b.status);
    if (ra !== rb) return ra - rb;
    return (a.name ?? '').localeCompare(b.name ?? '');
  });
}

// ---------------------------------------------------------------------------
// Ad-group expansion (CORE-M-11.b): lazy load per-campaign ad groups so a
// tenant with 30 campaigns does not trigger 30 backend reads on page open.
// The nested table renders name + status + last-30-days spend + conversions
// per ad group so an operator can see the shape without leaving Management.
// ---------------------------------------------------------------------------

const AD_GROUP_LIMIT = 50;

function useAdGroups(
  tenantId: string | null,
  campaignResourceName: string,
  enabled: boolean,
) {
  return useQuery<AdGroupPerformanceResponse>({
    queryKey: ['management-drilldown-adgroups', tenantId, campaignResourceName],
    queryFn: () =>
      listAdGroupPerformance({
        tenantId: tenantId ?? '',
        campaignResourceName,
        limit: AD_GROUP_LIMIT,
      }),
    enabled: enabled && Boolean(tenantId) && Boolean(campaignResourceName),
    staleTime: 60 * 1000,
    retry: 0,
    refetchOnWindowFocus: false,
  });
}

function formatConversions(v: number | null | undefined): string {
  if (typeof v !== 'number' || !Number.isFinite(v)) return '—';
  return v.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function formatUsdFromCostMicros(micros: number | null | undefined): string {
  if (typeof micros !== 'number' || !Number.isFinite(micros)) return '—';
  const usd = micros / 1_000_000;
  return `$${usd.toLocaleString(undefined, {
    maximumFractionDigits: usd >= 1000 ? 0 : 2,
  })}`;
}

/**
 * The seven-column nested table body a campaign expands into. Kept
 * inline (not a separate exported component) because the shape is
 * campaign-drill-only — extracting it earns nothing.
 */
function AdGroupsPanel({
  tenantId,
  campaignResourceName,
}: {
  tenantId: string | null;
  campaignResourceName: string;
}) {
  const q = useAdGroups(tenantId, campaignResourceName, true);
  if (q.isLoading) {
    return (
      <div className="flex items-center gap-2 px-4 py-3 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Reading ad groups...
      </div>
    );
  }
  if (q.isError) {
    return (
      <p className="px-4 py-3 text-xs text-destructive">
        Could not read ad groups for this campaign. Try again, or check
        the Google Ads connection in Settings, under Integrations.
      </p>
    );
  }
  const rows: readonly AdGroupPerformanceItem[] = q.data?.ad_groups ?? [];
  const windowStatus = q.data?.metrics_window?.status;
  if (rows.length === 0) {
    return (
      <p className="px-4 py-3 text-xs text-muted-foreground">
        No ad groups on this campaign.
      </p>
    );
  }
  return (
    <div className="border-t border-border bg-muted/10 px-6 py-3">
      <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {rows.length} ad group{rows.length === 1 ? '' : 's'}
          {q.data?.metrics_window
            ? ` · last ${(q.data.metrics_window as { days?: number }).days ?? 30} days`
            : ''}
        </span>
        {windowStatus === 'error' && (
          <span className="text-destructive">
            Metrics read failed — status columns may read as —
          </span>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[680px] border-collapse text-left text-xs">
          <thead className="border-b border-border text-[10px] uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-2 py-1.5 font-semibold">Ad group</th>
              <th className="px-2 py-1.5 font-semibold">Status</th>
              <th className="px-2 py-1.5 text-right font-semibold">Spend</th>
              <th className="px-2 py-1.5 text-right font-semibold">Clicks</th>
              <th className="px-2 py-1.5 text-right font-semibold">Conv.</th>
              <th className="px-2 py-1.5 text-right font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <AdGroupRow
                key={row.resource_name}
                tenantId={tenantId}
                campaignResourceName={campaignResourceName}
                row={row}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ad-group row + inner ads/keywords panel (CORE-M-11.c).
// ---------------------------------------------------------------------------

function useAds(
  tenantId: string | null,
  adGroupResourceName: string,
  enabled: boolean,
) {
  return useQuery<AdPerformanceResponse>({
    queryKey: ['management-drilldown-ads', tenantId, adGroupResourceName],
    queryFn: () =>
      listAdPerformance({
        tenantId: tenantId ?? '',
        adGroupResourceName,
        limit: 100,
      }),
    enabled: enabled && Boolean(tenantId) && Boolean(adGroupResourceName),
    staleTime: 60 * 1000,
    retry: 0,
    refetchOnWindowFocus: false,
  });
}

function useKeywords(
  tenantId: string | null,
  adGroupResourceName: string,
  enabled: boolean,
) {
  return useQuery<KeywordPerformanceResponse>({
    queryKey: ['management-drilldown-keywords', tenantId, adGroupResourceName],
    queryFn: () =>
      listKeywordPerformance({
        tenantId: tenantId ?? '',
        adGroupResourceName,
        limit: 200,
      }),
    enabled: enabled && Boolean(tenantId) && Boolean(adGroupResourceName),
    staleTime: 60 * 1000,
    retry: 0,
    refetchOnWindowFocus: false,
  });
}

function AdsAndKeywordsPanel({
  tenantId,
  adGroupResourceName,
}: {
  tenantId: string | null;
  adGroupResourceName: string;
}) {
  const adsQ = useAds(tenantId, adGroupResourceName, true);
  const kwQ = useKeywords(tenantId, adGroupResourceName, true);
  // CORE-M-11.d: read the shared ad-monitor cache to show ad_strength
  // per row. Same query key as the campaign-row chip, so this is a
  // cache hit whenever the drill-down has been opened once.
  const windowDays = useManagementWindowDays();
  const adHealth = useCampaignAdHealth(tenantId, windowDays);
  const strengthByAdId = useMemo(
    () => buildStrengthByAdId(adHealth.data?.ads ?? []),
    [adHealth.data],
  );

  const anyLoading = adsQ.isLoading || kwQ.isLoading;
  const anyError = adsQ.isError || kwQ.isError;

  if (anyLoading && !adsQ.data && !kwQ.data) {
    return (
      <div className="flex items-center gap-2 px-4 py-3 text-[11px] text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" />
        Reading ads and keywords...
      </div>
    );
  }
  return (
    <div className="grid gap-4 border-t border-border bg-muted/5 px-6 py-3 md:grid-cols-2">
      <AdsMiniTable
        tenantId={tenantId}
        adGroupResourceName={adGroupResourceName}
        ads={adsQ.data?.ads ?? []}
        error={adsQ.isError}
        strengthByAdId={strengthByAdId}
      />
      <KeywordsMiniTable
        tenantId={tenantId}
        adGroupResourceName={adGroupResourceName}
        keywords={kwQ.data?.keywords ?? []}
        error={kwQ.isError}
      />
      {anyError && !anyLoading && (
        <p className="text-[10px] text-destructive md:col-span-2">
          One of the reads failed — the affected side reads as empty.
        </p>
      )}
    </div>
  );
}

/**
 * CORE-M-11.d ad-strength join map. Indexed by MonitoredAd.ad_id so the
 * mini-table can look up strength by its ad_id (same key AdPerformanceItem
 * exposes). Exported so tests can assert the join key.
 */
export function buildStrengthByAdId(
  ads: readonly MonitoredAd[],
): Map<string, string> {
  const m = new Map<string, string>();
  for (const ad of ads) {
    if (ad.ad_id) m.set(String(ad.ad_id), (ad.ad_strength ?? '').toUpperCase());
  }
  return m;
}

/**
 * CORE-M-11.d strength chip inside the ads mini-table. Silent (renders
 * an em-dash) when the ad isn't in the ad-monitor set — that's how "not
 * yet loaded" and "genuinely no strength for this ad" look the same on
 * the render, which matches how Google Ads presents Draft/Learning ads.
 */
function AdStrengthCell({ strength }: { strength: string | undefined }) {
  const s = (strength ?? '').toUpperCase();
  if (!s) return <span className="text-muted-foreground">—</span>;
  if (s === 'POOR') {
    return (
      <Badge
        variant="outline"
        className="border-coral/40 bg-coral-light text-[10px] text-coral-dark"
      >
        Poor
      </Badge>
    );
  }
  if (s === 'AVERAGE') {
    return (
      <Badge
        variant="outline"
        className="border-amber-300 bg-amber-50 text-[10px] text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
      >
        Average
      </Badge>
    );
  }
  // Good / Excellent / anything else — plain text, no chip. Chans's tone
  // rule: silent on healthy states, loud on the ones that need attention.
  const label = s === 'GOOD' ? 'Good' : s === 'EXCELLENT' ? 'Excellent' : s;
  return <span className="text-muted-foreground">{label}</span>;
}

function AdRow({
  tenantId,
  adGroupResourceName,
  ad,
  strength,
}: {
  tenantId: string | null;
  adGroupResourceName: string;
  ad: AdPerformanceItem;
  strength: string | undefined;
}) {
  const status = (ad.status ?? '').toUpperCase();
  const label = ad.ad_name || ad.headlines[0] || ad.ad_id;
  const toggle = useDrilldownStatusToggle({
    level: 'ad',
    resourceName: ad.resource_name,
    name: label,
    status: ad.status,
    rereadKey: ['management-drilldown-ads', tenantId, adGroupResourceName],
  });
  return (
    <tr
      aria-busy={toggle.busy || undefined}
      className={cn(
        'border-b border-border/30 last:border-0',
        toggle.busy && BUSY_ROW_CLASS,
      )}
    >
      <td className="px-2 py-1 font-medium">{label}</td>
      <td className="px-2 py-1 text-muted-foreground">
        {status || 'UNKNOWN'}
      </td>
      <td className="px-2 py-1">
        <AdStrengthCell strength={strength} />
      </td>
      <td className="px-2 py-1 text-right tabular-nums">
        {formatUsdFromCostMicros(ad.metrics?.cost_micros)}
      </td>
      <td className="px-2 py-1 text-right tabular-nums">
        {formatConversions(ad.metrics?.conversions)}
      </td>
      <td className="px-2 py-1 text-right">{toggle.control}</td>
    </tr>
  );
}

function AdsMiniTable({
  tenantId,
  adGroupResourceName,
  ads,
  error,
  strengthByAdId,
}: {
  tenantId: string | null;
  adGroupResourceName: string;
  ads: readonly AdPerformanceItem[];
  error: boolean;
  strengthByAdId: Map<string, string>;
}) {
  return (
    <div>
      <h4 className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        Ads ({ads.length})
      </h4>
      {error ? (
        <p className="text-[11px] text-destructive">Could not read ads.</p>
      ) : ads.length === 0 ? (
        <p className="text-[11px] text-muted-foreground">No ads in this group.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left text-[11px]">
            <thead className="border-b border-border/60 text-[9px] uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-2 py-1 font-semibold">Ad</th>
                <th className="px-2 py-1 font-semibold">Status</th>
                <th className="px-2 py-1 font-semibold">Strength</th>
                <th className="px-2 py-1 text-right font-semibold">Spend</th>
                <th className="px-2 py-1 text-right font-semibold">Conv.</th>
                <th className="px-2 py-1 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {ads.slice(0, 20).map((a) => (
                <AdRow
                  key={a.resource_name}
                  tenantId={tenantId}
                  adGroupResourceName={adGroupResourceName}
                  ad={a}
                  strength={strengthByAdId.get(String(a.ad_id))}
                />
              ))}
            </tbody>
          </table>
          {ads.length > 20 && (
            <p className="mt-1 text-[10px] text-muted-foreground">
              ...and {ads.length - 20} more ad{ads.length - 20 === 1 ? '' : 's'}.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function KeywordRow({
  tenantId,
  adGroupResourceName,
  keyword,
}: {
  tenantId: string | null;
  adGroupResourceName: string;
  keyword: KeywordPerformanceItem;
}) {
  const toggle = useDrilldownStatusToggle({
    level: 'keyword',
    resourceName: keyword.resource_name,
    name: keyword.keyword_text ?? 'this keyword',
    status: keyword.status,
    rereadKey: ['management-drilldown-keywords', tenantId, adGroupResourceName],
  });
  return (
    <tr
      aria-busy={toggle.busy || undefined}
      className={cn(
        'border-b border-border/30 last:border-0',
        toggle.busy && BUSY_ROW_CLASS,
      )}
    >
      <td className="px-2 py-1 font-medium">{keyword.keyword_text ?? '—'}</td>
      <td className="px-2 py-1 text-muted-foreground">
        {keyword.match_type ?? '—'}
      </td>
      <td className="px-2 py-1 text-muted-foreground">
        {(keyword.status ?? '').toUpperCase() || 'UNKNOWN'}
      </td>
      <td className="px-2 py-1 text-right tabular-nums">
        {formatUsdFromCostMicros(keyword.metrics?.cost_micros)}
      </td>
      <td className="px-2 py-1 text-right tabular-nums">
        {formatConversions(keyword.metrics?.conversions)}
      </td>
      <td className="px-2 py-1 text-right">{toggle.control}</td>
    </tr>
  );
}

function KeywordsMiniTable({
  tenantId,
  adGroupResourceName,
  keywords,
  error,
}: {
  tenantId: string | null;
  adGroupResourceName: string;
  keywords: readonly KeywordPerformanceItem[];
  error: boolean;
}) {
  return (
    <div>
      <h4 className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        Keywords ({keywords.length})
      </h4>
      {error ? (
        <p className="text-[11px] text-destructive">Could not read keywords.</p>
      ) : keywords.length === 0 ? (
        <p className="text-[11px] text-muted-foreground">
          No keywords in this group.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left text-[11px]">
            <thead className="border-b border-border/60 text-[9px] uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-2 py-1 font-semibold">Keyword</th>
                <th className="px-2 py-1 font-semibold">Match</th>
                <th className="px-2 py-1 font-semibold">Status</th>
                <th className="px-2 py-1 text-right font-semibold">Spend</th>
                <th className="px-2 py-1 text-right font-semibold">Conv.</th>
                <th className="px-2 py-1 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {keywords.slice(0, 20).map((k) => (
                <KeywordRow
                  key={k.resource_name}
                  tenantId={tenantId}
                  adGroupResourceName={adGroupResourceName}
                  keyword={k}
                />
              ))}
            </tbody>
          </table>
          {keywords.length > 20 && (
            <p className="mt-1 text-[10px] text-muted-foreground">
              ...and {keywords.length - 20} more keyword
              {keywords.length - 20 === 1 ? '' : 's'}.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function AdGroupRow({
  tenantId,
  campaignResourceName,
  row,
}: {
  tenantId: string | null;
  campaignResourceName: string;
  row: AdGroupPerformanceItem;
}) {
  const [expanded, setExpanded] = useState(false);
  const status = (row.status ?? '').toUpperCase();
  const isEnabled = status === 'ENABLED';
  const metrics = row.metrics;
  const toggle = useDrilldownStatusToggle({
    level: 'ad_group',
    resourceName: row.resource_name,
    name: row.name ?? row.ad_group_id,
    status: row.status,
    rereadKey: ['management-drilldown-adgroups', tenantId, campaignResourceName],
  });
  return (
    <>
      <tr
        aria-busy={toggle.busy || undefined}
        className={cn(
          'border-b border-border/40 last:border-0',
          toggle.busy && BUSY_ROW_CLASS,
        )}
      >
        <td className="px-2 py-1.5 font-medium">
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              aria-label={expanded ? 'Collapse ads and keywords' : 'Expand ads and keywords'}
              aria-expanded={expanded}
              onClick={() => setExpanded((v) => !v)}
              className="rounded p-0.5 text-muted-foreground hover:bg-muted/40 hover:text-foreground"
            >
              {expanded ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
            </button>
            {row.name ?? row.ad_group_id}
          </div>
        </td>
        <td className="px-2 py-1.5">
          <Badge
            variant="outline"
            className={cn(
              'text-[10px]',
              isEnabled && 'border-teal/40 bg-teal-light text-teal-dark',
              !isEnabled && 'border-border bg-muted/40 text-muted-foreground',
            )}
          >
            {status || 'UNKNOWN'}
          </Badge>
        </td>
        <td className="px-2 py-1.5 text-right tabular-nums">
          {formatUsdFromCostMicros(metrics?.cost_micros)}
        </td>
        <td className="px-2 py-1.5 text-right tabular-nums">
          {typeof metrics?.clicks === 'number'
            ? metrics.clicks.toLocaleString()
            : '—'}
        </td>
        <td className="px-2 py-1.5 text-right tabular-nums">
          {formatConversions(metrics?.conversions)}
        </td>
        <td className="px-2 py-1.5 text-right">{toggle.control}</td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={6} className="p-0">
            <AdsAndKeywordsPanel
              tenantId={tenantId}
              adGroupResourceName={row.resource_name}
            />
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * CORE-M-11.f: per-row "Copy link" button that copies the M-11.e
 * deep-link URL (`/ads-tools/management?platform=google-ads&campaign=<id>`)
 * to the operator's clipboard. Replaces the retired "Details" button,
 * which pointed at the (now-defunct) standalone Current Campaigns
 * route.
 *
 * Failure mode: `navigator.clipboard` is guarded — an old browser or a
 * non-secure context (jsdom in tests, non-HTTPS locally) surfaces an
 * error toast instead of silently pretending it worked.
 */
export function buildCampaignDeepLink(campaignId: string): string {
  const origin =
    typeof window !== 'undefined' && window.location
      ? window.location.origin
      : '';
  return `${origin}/ads-tools/management?platform=google-ads&campaign=${encodeURIComponent(campaignId)}`;
}

function CampaignCopyLinkButton({ campaignId }: { campaignId: string | null }) {
  const [copied, setCopied] = useState(false);
  const disabled = !campaignId;

  const handleCopy = async () => {
    if (!campaignId) return;
    const url = buildCampaignDeepLink(campaignId);
    try {
      if (
        typeof navigator === 'undefined' ||
        !navigator.clipboard ||
        typeof navigator.clipboard.writeText !== 'function'
      ) {
        throw new Error('clipboard-unavailable');
      }
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Link copied — paste it in a browser tab to open the drill-down on this campaign.');
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy — your browser blocked clipboard access. Right-click the row and inspect element for the campaign id.');
    }
  };

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={handleCopy}
      disabled={disabled}
      title={
        disabled
          ? 'This campaign has no id on record — nothing to copy.'
          : 'Copy a shareable URL that opens the drill-down here on this campaign.'
      }
      className="inline-flex items-center gap-1 text-xs"
      data-testid="copy-campaign-link"
    >
      {copied ? (
        <>
          <Check className="h-3 w-3" aria-hidden="true" />
          Copied
        </>
      ) : (
        <>
          <Link2 className="h-3 w-3" aria-hidden="true" />
          Copy link
        </>
      )}
    </Button>
  );
}

interface CampaignRowProps {
  campaign: LiveCampaignInventoryItem;
  tenantId: string | null;
  adStrength: CampaignAdStrengthSummary | undefined;
  /**
   * CORE-M-11.e: when true, the ad-groups panel renders expanded on mount
   * and the row scrolls itself into view. Set by the top-level component
   * for the campaign whose id matches the `?campaign` URL param.
   */
  defaultExpanded?: boolean;
  /** CORE-M-11.e: paint a temporary highlight class so the operator's eye
   *  lands on the right row when a deep link brings them here. */
  highlighted?: boolean;
}

function CampaignRow({
  campaign,
  tenantId,
  adStrength,
  defaultExpanded = false,
  highlighted = false,
}: CampaignRowProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const rowRef = useRef<HTMLTableRowElement>(null);
  const status = (campaign.status ?? 'unknown').toUpperCase();
  const isEnabled = status === 'ENABLED';
  const isPaused = status === 'PAUSED';

  const toggle = useDrilldownStatusToggle({
    level: 'campaign',
    resourceName: campaign.resource_name,
    name: campaign.name,
    status: campaign.status,
    rereadKey: ['management-campaigns-drilldown', tenantId],
    // Rows Core knows about that are not live in Google Ads have nothing
    // to pause.
    live: campaign.live_in_google_ads !== false && campaign.manageable !== false,
  });

  const budgetLabel = formatUsdFromMicros(
    campaign.daily_budget_micros ?? campaign.planned_daily_budget_micros ?? null,
  );

  // CORE-M-11.e: on mount, if this is the deep-linked row, scroll it into
  // view. Guarded on the initial defaultExpanded so subsequent user
  // clicks on the same row don't retrigger the scroll.
  useEffect(() => {
    if (defaultExpanded && rowRef.current) {
      rowRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <tr
        ref={rowRef}
        data-campaign-id={campaign.campaign_id ?? ''}
        data-highlighted={highlighted ? 'true' : undefined}
        aria-busy={toggle.busy || undefined}
        className={cn(
          'border-b border-border/60 last:border-0 hover:bg-muted/20',
          highlighted && 'bg-coral-light/30',
          toggle.busy && BUSY_ROW_CLASS,
        )}>
        <td className="px-3 py-2 text-sm">
          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label={expanded ? 'Collapse ad groups' : 'Expand ad groups'}
              aria-expanded={expanded}
              onClick={() => setExpanded((v) => !v)}
              className="rounded p-0.5 text-muted-foreground hover:bg-muted/40 hover:text-foreground"
            >
              {expanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
            <div>
              <div className="flex flex-wrap items-center gap-1.5 font-medium">
                <span>{campaign.name}</span>
                <AdStrengthChip summary={adStrength} />
              </div>
              <div className="font-mono text-[11px] text-muted-foreground">
                {campaign.advertising_channel_type ?? '—'}
              </div>
            </div>
          </div>
        </td>
        <td className="px-3 py-2 text-sm">
          <Badge
            variant="outline"
            className={cn(
              isEnabled && 'border-teal/40 bg-teal-light text-teal-dark',
              isPaused && 'border-border bg-muted/40 text-muted-foreground',
              !isEnabled && !isPaused && 'border-coral/40 bg-coral-light text-coral-dark',
            )}
          >
            {isEnabled ? 'Enabled' : isPaused ? 'Paused' : status}
          </Badge>
        </td>
        <td className="px-3 py-2 text-sm tabular-nums">{budgetLabel}</td>
        <td className="px-3 py-2 text-sm text-muted-foreground">
          {campaign.source_label}
        </td>
        <td className="px-3 py-2 text-right">
          <CampaignCopyLinkButton campaignId={campaign.campaign_id ?? null} />
        </td>
        <td className="px-3 py-2 text-right">{toggle.control}</td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={6} className="p-0">
            <AdGroupsPanel
              tenantId={tenantId}
              campaignResourceName={campaign.resource_name}
            />
          </td>
        </tr>
      )}
    </>
  );
}

function useCampaigns(tenantId: string | null) {
  return useQuery({
    queryKey: ['management-campaigns-drilldown', tenantId],
    queryFn: () =>
      listLiveCampaigns({ tenantId: tenantId ?? '', includeMetrics: false }),
    enabled: Boolean(tenantId),
    staleTime: 60 * 1000,
    retry: 0,
    refetchOnWindowFocus: false,
  });
}

// ---------------------------------------------------------------------------
// CORE-M-10.a: per-campaign ad-strength roll-up. Reads the same ad-monitor
// endpoint the Ad Review tab uses (shared cached read via matching
// queryKey) and buckets each campaign's ads by strength so the row can
// show a "below Good" chip with a plain-English reason. Chans's rule
// (§2.10): 'poor' with no reason is useless.
// ---------------------------------------------------------------------------

/** Google Ads' `ad_strength` values that count as "below Good" per spec §2.10. */
const AD_STRENGTH_BELOW_GOOD = new Set(['POOR', 'AVERAGE']);

export interface CampaignAdStrengthSummary {
  belowGoodCount: number;
  totalCount: number;
  /** Worst tier seen among this campaign's ads: 'POOR' beats 'AVERAGE'. */
  worst: 'POOR' | 'AVERAGE' | null;
}

export function summarizeAdStrengthByCampaign(
  ads: readonly MonitoredAd[],
): Map<string, CampaignAdStrengthSummary> {
  const byCampaign = new Map<string, CampaignAdStrengthSummary>();
  for (const ad of ads) {
    const key = String(ad.campaign_id ?? '');
    if (!key) continue;
    const entry = byCampaign.get(key) ?? {
      belowGoodCount: 0,
      totalCount: 0,
      worst: null,
    };
    entry.totalCount += 1;
    const strength = (ad.ad_strength ?? '').toUpperCase();
    if (AD_STRENGTH_BELOW_GOOD.has(strength)) {
      entry.belowGoodCount += 1;
      if (strength === 'POOR') entry.worst = 'POOR';
      else if (entry.worst !== 'POOR') entry.worst = 'AVERAGE';
    }
    byCampaign.set(key, entry);
  }
  return byCampaign;
}

// Gap A / CORE-M-12: was a local `const AD_HEALTH_WINDOW_DAYS = 30`. The
// comment below claimed one cached read covered this and the Ad Review tab —
// true only while both hardcoded the same number, and silently false the
// moment the page selector moved.
function useCampaignAdHealth(tenantId: string | null, windowDays: number) {
  // Same queryKey shape the Ad Review tab uses — one cached network read
  // per workspace-window covers both consumers.
  return useQuery({
    queryKey: ['ga-ad-monitor', tenantId, windowDays],
    queryFn: () => getAdMonitor(tenantId ?? '', windowDays),
    enabled: Boolean(tenantId),
    staleTime: 60 * 1000,
    retry: 0,
    refetchOnWindowFocus: false,
  });
}

/** The chip that surfaces on a campaign row when any of its ads read as
 *  below-Good ad-strength. Silent when the campaign is healthy or the
 *  ad-monitor read has not resolved / has no ads for this campaign. */
function AdStrengthChip({ summary }: { summary: CampaignAdStrengthSummary | undefined }) {
  if (!summary || summary.belowGoodCount === 0) return null;
  const worst = summary.worst ?? 'AVERAGE';
  const label =
    worst === 'POOR'
      ? `${summary.belowGoodCount} ad${summary.belowGoodCount === 1 ? '' : 's'} at Poor strength`
      : `${summary.belowGoodCount} ad${summary.belowGoodCount === 1 ? '' : 's'} below Good`;
  return (
    <Badge
      variant="outline"
      title={
        worst === 'POOR'
          ? 'At least one ad in this campaign reads as Poor strength in Google Ads. Open the drill-down and check the ads column.'
          : 'At least one ad in this campaign reads as Average strength in Google Ads. Open the drill-down and check the ads column.'
      }
      className={cn(
        'text-[10px]',
        worst === 'POOR'
          ? 'border-coral/40 bg-coral-light text-coral-dark'
          : 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
      )}
    >
      {label}
    </Badge>
  );
}

export function CampaignDrilldown({ tenantId: tenantIdProp }: CampaignDrilldownProps) {
  const storeTenant = useTenantStore((s) => s.currentTenant);
  const tenantId = (tenantIdProp === undefined ? storeTenant : tenantIdProp) ?? null;
  const [filter, setFilter] = useState<StatusFilter>('all');
  const q = useCampaigns(tenantId);

  // CORE-M-11.e: deep-link support. `?campaign=<id>` on
  // /ads-tools/management auto-opens the drill-down, auto-expands that
  // campaign's ad-groups panel, and scrolls it into view. Consumed once
  // on mount: subsequent clicks on the drilldown don't rewrite the URL.
  const [searchParams] = useSearchParams();
  const deepLinkCampaignId = searchParams.get('campaign') || null;
  // CORE-M-10.a: shared ad-monitor read for the per-campaign "below Good"
  // chip. Same queryKey as the Ad Review tab so this is a cached read.
  const windowDays = useManagementWindowDays();
  const adHealth = useCampaignAdHealth(tenantId, windowDays);
  const adStrengthByCampaign = useMemo(
    () => summarizeAdStrengthByCampaign(adHealth.data?.ads ?? []),
    [adHealth.data],
  );
  // Gap B wave 1a: an Undo toast made for one client must never act on it
  // after a switch. The cleanup runs when the client changes and when the
  // drill-down unmounts (a switch made on another page lands there).
  useEffect(() => () => dismissAllUndoToasts(), [tenantId]);

  // Gap B wave 1a: the same ad-monitor read carries write_capabilities(),
  // which answers for all four levels' pause/enable.
  const writeAccess: DrilldownWriteAccess = useMemo(
    () => ({
      tenantId,
      capabilities: adHealth.data?.capabilities,
      capabilitiesState: adHealth.isLoading
        ? 'loading'
        : adHealth.isError
          ? 'error'
          : 'ready',
    }),
    [tenantId, adHealth.data, adHealth.isLoading, adHealth.isError],
  );

  const rows: LiveCampaignInventoryItem[] = useMemo(() => {
    const all = q.data?.campaigns ?? [];
    const visible =
      filter === 'enabled'
        ? all.filter((c) => (c.status ?? '').toUpperCase() === 'ENABLED')
        : all;
    return sortEnabledFirst(visible);
  }, [q.data, filter]);

  if (!tenantId) {
    return null; // The tab-body no-workspace guards already speak here.
  }

  const enabledCount = (q.data?.campaigns ?? []).filter(
    (c) => (c.status ?? '').toUpperCase() === 'ENABLED',
  ).length;
  const pausedCount = (q.data?.campaigns ?? []).filter(
    (c) => (c.status ?? '').toUpperCase() === 'PAUSED',
  ).length;
  const totalCount = q.data?.campaigns.length ?? 0;

  return (
    <details
      className="rounded-md border border-border bg-warm-bg"
      // CORE-M-11.e: open on mount when a deep link brought the operator
      // here. `open` is an uncontrolled default in React (not a live prop)
      // so subsequent clicks toggle freely; this only sets the initial
      // state.
      open={Boolean(deepLinkCampaignId)}
    >
      <summary className="flex cursor-pointer items-center gap-2 px-4 py-3 text-sm font-semibold">
        <ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90" />
        Campaign structure
        <span className="ml-2 font-normal text-muted-foreground">
          {q.isLoading
            ? 'reading...'
            : totalCount
              ? `${enabledCount} enabled, ${pausedCount} paused${totalCount > enabledCount + pausedCount ? ', ' + (totalCount - enabledCount - pausedCount) + ' other' : ''}`
              : 'no campaigns'}
        </span>
      </summary>
      <DrilldownWriteContext.Provider value={writeAccess}>
      <div className="border-t border-border p-4">
        {q.isLoading && (
          <AdsSlowLoad label="Reading campaigns" tenantId={tenantId} />
        )}

        {q.isError && (
          <Card>
            <CardHeader>
              <CardTitle>Could not read campaigns</CardTitle>
              <CardDescription>
                Core could not reach the Google Ads campaigns read for{' '}
                <span className="font-medium text-foreground">{tenantId}</span>.
                Check the Google Ads connection in Settings, under
                Integrations, then reload.
              </CardDescription>
            </CardHeader>
          </Card>
        )}

        {q.isSuccess && (
          <>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-1 rounded-md border border-border p-1 text-xs">
                <button
                  type="button"
                  onClick={() => setFilter('enabled')}
                  className={cn(
                    'rounded px-2 py-1 font-medium',
                    filter === 'enabled'
                      ? 'bg-coral text-white'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  Enabled
                </button>
                <button
                  type="button"
                  onClick={() => setFilter('all')}
                  className={cn(
                    'rounded px-2 py-1 font-medium',
                    filter === 'all'
                      ? 'bg-coral text-white'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  All ({totalCount})
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                Clear the alerts in the tabs above first, then skim the
                structure here.
              </p>
            </div>

            {rows.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {filter === 'enabled'
                  ? 'No enabled campaigns on this account.'
                  : 'No campaigns on this account.'}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[820px] border-collapse text-left">
                  <thead className="border-b border-border text-[11px] uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 font-semibold">Campaign</th>
                      <th className="px-3 py-2 font-semibold">Status</th>
                      <th className="px-3 py-2 font-semibold">Daily budget</th>
                      <th className="px-3 py-2 font-semibold">Source</th>
                      <th className="px-3 py-2 font-semibold text-right">Link</th>
                      <th className="px-3 py-2 font-semibold text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((c) => {
                      const isDeepLinked =
                        deepLinkCampaignId != null &&
                        String(c.campaign_id ?? '') === deepLinkCampaignId;
                      return (
                        <CampaignRow
                          key={c.resource_name}
                          campaign={c}
                          tenantId={tenantId}
                          adStrength={adStrengthByCampaign.get(
                            String(c.campaign_id ?? ''),
                          )}
                          defaultExpanded={isDeepLinked}
                          highlighted={isDeepLinked}
                        />
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <p className="mt-3 text-xs text-muted-foreground">
              Click the arrow on a campaign to see its ad groups; click again
              on an ad group to see its ads and keywords. Campaigns with ads
              below Good strength carry an inline chip on the row. "Copy
              link" copies a URL that opens the drill-down here on the same
              campaign.
            </p>
          </>
        )}
      </div>
      </DrilldownWriteContext.Provider>
    </details>
  );
}

export default CampaignDrilldown;
